import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowRight, lucideX } from '@ng-icons/lucide';
import { HlmBadgeImports, type BadgeVariants } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmProgressImports } from '@spartan-ng/helm/progress';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { CompanyService } from '../../../../services/company.service';
import { SetupProgressService, SetupTone } from '../../../../services/setup-progress.service';

/** How many open tasks the compact popover lists before linking to the full one. */
const MAX_PREVIEW_TASKS = 3;

/** Tone → badge variant, so the phase reads from the theme instead of ad-hoc colors. */
const TONE_BADGE: Record<SetupTone, BadgeVariants['variant']> = {
  required: 'default',
  optional: 'secondary',
  done: 'outline',
};

/**
 * Onboarding checklist summary. Rendered once in the notification popover and
 * once in the mobile sheet — previously this markup existed three times.
 */
@Component({
  selector: 'app-setup-progress',
  imports: [
    RouterLink,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmProgressImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideArrowRight, lucideX })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': '_hostClasses()' },
  template: `
    <div class="flex items-center justify-between gap-2">
      <p hlmSmall>{{ status().title }}</p>
      <span hlmBadge [variant]="badgeVariant()">{{ status().percent }}%</span>
    </div>

    <div hlmProgress class="mt-2" [value]="status().percent">
      <div hlmProgressIndicator></div>
    </div>

    <p hlmMuted class="mt-1.5">{{ status().subtitle }}</p>

    <!-- The open tasks are listed right here, so the popover is actionable on
         its own instead of only pointing at the checklist. -->
    @if (previewTasks().length > 0) {
      <ul class="mt-2.5 space-y-0.5">
        @for (task of previewTasks(); track task.id) {
          <li class="flex items-center gap-1">
            <a
              class="hover:bg-accent focus-visible:ring-ring/50 flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus-visible:ring-[3px]"
              [routerLink]="task.routerLink"
              [queryParams]="task.queryParams"
              [fragment]="task.fragment"
              (click)="navigated.emit()">
              <span class="truncate">{{ task.title }}</span>
              <ng-icon
                name="lucideArrowRight"
                class="text-muted-foreground ms-auto shrink-0"
                data-icon="inline-end" />
            </a>
            <!-- Same as "Nicht nötig" on the checklist: only optional tasks can be closed. -->
            @if (!task.required) {
              <button
                hlmBtn
                variant="ghost"
                size="icon-xs"
                class="text-muted-foreground shrink-0"
                aria-label="Aufgabe ausblenden"
                title="Aufgabe ausblenden"
                [disabled]="dismissingTaskId() === task.id"
                (click)="dismiss(task.id)">
                <ng-icon name="lucideX" />
              </button>
            }
          </li>
        }
      </ul>
    }

    <!-- Always offered: the checklist un-hides itself when reached this way,
         so this link can never point at an empty page. -->
    <a
      hlmBtn
      variant="link"
      size="xs"
      class="mt-1 px-0"
      routerLink="/"
      fragment="setup-checklist"
      (click)="navigated.emit()">
      {{
        hiddenTaskCount() > 0 ? 'Alle ' + openTasks().length + ' Aufgaben ansehen' : 'Zur Übersicht'
      }}
      <ng-icon name="lucideArrowRight" data-icon="inline-end" />
    </a>
  `,
})
export class SetupProgressComponent {
  private readonly setup = inject(SetupProgressService);
  private readonly companyService = inject(CompanyService);

  /** Wraps the block in a card — used inside the mobile sheet. */
  readonly bordered = input(false, { transform: booleanAttribute });

  /** Emitted when the user follows the link, so overlays can close themselves. */
  readonly navigated = output<void>();

  readonly status = this.setup.status;

  readonly openTasks = this.setup.openTasks;

  /** Keep the popover compact — the rest stays one click away in the checklist. */
  protected readonly previewTasks = computed(() => this.openTasks().slice(0, MAX_PREVIEW_TASKS));

  protected readonly hiddenTaskCount = computed(() =>
    Math.max(0, this.openTasks().length - MAX_PREVIEW_TASKS),
  );

  protected readonly dismissingTaskId = signal<string | null>(null);

  /** Hide an optional task for good, like "Nicht nötig" on the checklist. */
  protected dismiss(taskId: string): void {
    if (this.dismissingTaskId()) return;
    this.dismissingTaskId.set(taskId);
    this.companyService.dismissSetupTask(taskId).subscribe({
      next: () => this.dismissingTaskId.set(null),
      error: () => this.dismissingTaskId.set(null),
    });
  }

  protected readonly badgeVariant = computed(() => TONE_BADGE[this.status().tone]);

  protected readonly _hostClasses = computed(() =>
    this.bordered() ? 'border-border block rounded-lg border p-3' : 'block',
  );
}
